import React from "react";
import { useSEO } from "@/hooks/useSEO";
import { Section, SectionHeading, Reveal, CTASection } from "@/components/ui-bits";
import { PageHero, ProcessStep, CertCard } from "@/components/shared";
import { QUALITY_FLOW, CERTS } from "@/lib/data";

const PRINCIPLES = [
  { title: "Hygiene", desc: "Controlled, sanitary production environments." },
  { title: "Process Control", desc: "Defined, repeatable manufacturing steps." },
  { title: "Batch Consistency", desc: "Consistent output across production runs." },
  { title: "Documentation", desc: "Recorded processes and specifications." },
  { title: "Traceability", desc: "Batch-level traceability through production." },
  { title: "Finished-Product Controls", desc: "Checks before product leaves the facility." },
];

export default function Quality() {
  useSEO("Food Safety & Quality Manufacturing | Wisperwick Studio",
    "Quality built into every step — raw material control, in-process checks, batch traceability and finished-product controls, aligned to food-safety principles.");
  const steps = QUALITY_FLOW.map((t, i) => ({ n: String(i + 1).padStart(2, "0"), title: t, desc: "" }));
  return (
    <div>
      <PageHero eyebrow="Quality" title="Quality isn't a final check. It's the process."
        sub="Quality begins before production and continues throughout the manufacturing process." />

      <Section className="pt-0">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="label text-ws-accent mb-8">The quality system</p>
            {steps.map((s, i) => <ProcessStep key={s.n} step={s} index={i} last={i === steps.length - 1} />)}
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <SectionHeading title="Built into every step." />
            <div className="grid sm:grid-cols-2 gap-4 mt-8">
              {PRINCIPLES.map((p, i) => (
                <Reveal key={p.title} delay={(i % 2) * 0.06}>
                  <div className="border border-ws-border rounded-xl p-6 bg-ws-card h-full">
                    <h3 className="font-display text-xl text-ws-dark">{p.title}</h3>
                    <p className="text-ws-text2 text-sm mt-2 leading-relaxed">{p.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </Section>

      <Section bg="var(--ws-bg2)">
        <SectionHeading eyebrow="Certifications & food safety" title="Built around food safety." />
        <div data-testid="quality-standards" className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-10 sm:mt-14">
          {CERTS.map((c) => <CertCard key={c} name={c} />)}
        </div>
      </Section>

      <CTASection />
    </div>
  );
}
